import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {
    AdditionalCategories,
    ColumnFormatterService,
    CompDefinitionAvailabilities,
    Filter,
    Menu,
    MenuCategory,
    MenuItem,
    Organization,
    ReportColumns,
    UnderscoreConversionPipe
} from 'brewbill-lib';

@Component({
    selector: 'bb-select-filter',
    templateUrl: './select-filter.component.html',
    styleUrls: ['./select-filter.component.scss'],
})
export class SelectFilterComponent implements OnInit, OnChanges {
    @Input() filter: Filter;
    @Input() menu: Menu;
    @Input() organization: Organization;
    @Output() remove = new EventEmitter();
    @Output() edit = new EventEmitter();

    value = '';

    constructor(
        private underscoreConversionPipe: UnderscoreConversionPipe,
        private columnFormatterService: ColumnFormatterService
    ) {
    }

    ngOnInit(): void {
        this.parseValue();
    }

    ngOnChanges(changes: SimpleChanges) {
        this.parseValue();
    }

    parseValue() {
        if (!!this.filter) {
            switch (this.filter.operator) {
            case 'IN':
                this.value = `In ${this.parseLabel()}`;
                break;
            case 'NOT_IN':
                this.value = `Not In ${this.parseLabel()}`;
                break;
            case 'EQUALS':
                this.value = `Equal To ${this.filter.values[0].value}`;
                break;
            case 'NOT_EQUALS':
                this.value = `Not Equal To ${this.filter.values[0].value}`;
                break;
            case 'GREATER_THAN':
                this.value = `Greater Than ${this.filter.values[0].value}`;
                break;
            case 'GREATER_THAN_OR_EQUAL':
                this.value = `Greater Than Or Equal To ${this.filter.values[0].value}`;
                break;
            case 'LESS_THAN':
                this.value = `Less Than ${this.filter.values[0].value}`;
                break;
            case 'LESS_THAN_OR_EQUAL':
                this.value = `Less Than Or Equal To ${this.filter.values[0].value}`;
                break;
            }
        }
    }

    parseLabel(): string {
        if (!!this.filter) {
            switch (this.filter.field) {
            case 'MENU_LABEL_ID':
                return this.menu.menuLabels.filter(f => this.filter.values.some(v => parseInt(v.value, 10) === f.id))
                    .map(l => {
                        return l.name;
                    }).join(', ');

            case 'MENU_CATEGORY_ID':
                const categories = [
                    ...this.menu.menuCategories,
                    new MenuCategory({id: AdditionalCategories.UNCATEGORIZED.id, name: AdditionalCategories.UNCATEGORIZED.label}),
                    new MenuCategory({id: AdditionalCategories.MEMBERSHIPS.id, name: AdditionalCategories.MEMBERSHIPS.label}),
                    new MenuCategory({id: AdditionalCategories.GIFT_CARDS.id, name: AdditionalCategories.GIFT_CARDS.label})
                ];
                return categories.filter(f => this.filter.values.some(v => parseInt(v.value, 10) === f.id))
                    .map(l => {
                        return l.name;
                    }).join(', ');

            case 'MENU_ITEM_ID':
                const items = [
                    ...this.menu.menuItems,
                    new MenuItem({id: AdditionalCategories.UNCATEGORIZED.id, name: AdditionalCategories.UNCATEGORIZED.label}),
                    new MenuItem({id: AdditionalCategories.MEMBERSHIPS.id, name: AdditionalCategories.MEMBERSHIPS.label}),
                    new MenuItem({id: AdditionalCategories.GIFT_CARDS.id, name: AdditionalCategories.GIFT_CARDS.label})
                ];
                return items.filter(f => this.filter.values.some(v => parseInt(v.value, 10) === f.id))
                    .map(l => {
                        return l.name;
                    }).join(', ');

            case 'PICKUP_LOCATION_ID':
                return this.organization.pickupLocations.filter(p => this.filter.values.some(v => parseInt(v.value, 10) === p.id))
                    .map(l => {
                        return l.name;
                    }).join(', ');

            case 'COMP_DEFINITION_ID':
                return this.organization.compDefinitions.filter(p => this.filter.values.some(v => parseInt(v.value, 10) === p.id))
                    .map(l => {
                        return l.name;
                    }).join(', ');

            case 'ORDER_ITEM_COMP_DEFINITION_ID':
                return this.organization.compDefinitions.filter(p => p.availability === CompDefinitionAvailabilities.ORDER_ITEM
                    && this.filter.values.some(v => parseInt(v.value, 10) === p.id))
                    .map(l => {
                        return l.name;
                    }).join(', ');

            case 'ORDER_ITEM_SELECTED_PRICE_NAME':
                const prices = this.menu.menuItems.reduce((acc, i) => {
                    i.prices.forEach(p => {
                        if (!acc.some(a => a.id === p.name)) {
                            acc.push({id: p.name, label: p.name});
                        }
                    });
                    return acc;
                }, []);
                return prices.filter(p => this.filter.values.some(v => v.value === p.id))
                    .map(l => {
                        return l.label;
                    }).join(', ');
            case 'ORDER_STATUS':
            case 'PAYMENT_INFORMATION_PAYMENT_STATUS':
            case 'PAYMENT_INFORMATION_PAYMENT_TYPE':
                return this.filter.values.map(v => this.underscoreConversionPipe.transform(v.value)).join(', ');

            case 'MEMBERSHIP_RENEWAL_MEMBERSHIP_PROGRAM_ID':
                const programs = [
                    ...this.organization.membershipPrograms
                ];
                return programs.filter(f => this.filter.values.some(v => parseInt(v.value, 10) === f.id))
                    .map(l => {
                        return l.name;
                    }).join(', ');
            default:
                const column = ReportColumns.find(c => c.key === this.filter.field);
                if (!!column) {
                    return this.filter.values.map(v => this.columnFormatterService.formatValueWithFormat(v.value, column.format))
                        .join(', ');
                } else {
                    return this.filter.values.map(v => v.value).join(', ');
                }
            }
        }
        return '';
    }

    removeClicked(event) {
        event.stopPropagation();
        this.remove.emit();
    }
}
